import React, { useState, useEffect, useMemo } from 'react';
import { Select } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConfig } from '../../../../../../app/configcontext';
import { useSelector, useDispatch } from 'react-redux';
import { businessSlice } from '../../../../../../features/business/businessSlice';
import ProtectedPaymentRoute from '../../../../../auth/Auth';
import { validateAlphabets } from '../../../../../../utils/Validation';

export default function BusinessName() {
  ProtectedPaymentRoute();

  const config = useConfig();
  const designatorList = useMemo(() => config?.designator || [], [config?.designator]);
  const actions = businessSlice.actions;
  const dispatch = useDispatch();
  const location = useLocation();
  const business = useSelector((state) => state.business);
  const businessTypeId = business?.businessTypeId;
  const businessDetails = business?.businessDetails;
  const designatorWithLabel = designatorList.map((item) => ({ label: item.name }));

  const designatorObjectFromId = () => {
    if (!businessDetails?.designatorId || designatorList.length === 0) return null;
    const match = designatorList.find((item) => item.id === businessDetails.designatorId);
    return match ? { label: match.name } : null;
  };

  const savedDesignator =
    designatorObjectFromId() ||
    (designatorList.length > 0 ? { label: designatorList[0].name } : null);

  const savedBusinessName = businessDetails ? businessDetails.businessName : "";

  const [businessName, setBusinessName] = useState(savedBusinessName);
  const [selectedDesignator, setSelectedDesignator] = useState(savedDesignator ?? null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('selectedDesignator');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setSelectedDesignator(
            parsed.length > 0 ? { label: String(parsed[0]) } : null
          );
        } else if (parsed && typeof parsed.label === 'string') {
          setSelectedDesignator(parsed);
        }
      } catch {
        // ignore bad localStorage
      }
      return;
    }
    if (designatorList.length === 0) return;

    if (businessDetails?.designatorId) {
      const match = designatorList.find(
        (item) => item.id === businessDetails.designatorId
      );
      if (match) {
        setSelectedDesignator({ label: match.name });
        return;
      }
    }
    setSelectedDesignator({ label: designatorList[0].name });
  }, [designatorList, businessDetails?.designatorId]);

  const handleBusinessNameChange = (e) => {
    const name = e.target.value;

    if (validateAlphabets(name) || name === '') {
      setBusinessName(name);
      setErrors({ ...errors, businessName: '' });
    } else {
      setErrors({ ...errors, businessName: 'Business name should contain only alphabets.' });
    }
  };

  const handleDesignatorChange = (_, newValue) => {
    setSelectedDesignator(newValue);
    setErrors({ ...errors, designator: '' });
  };

  const handleNextButtonClick = async (event) => {
    event.preventDefault();

    const newErrors = {};

    if (!businessName.trim()) {
      newErrors.businessName = 'Please enter the business name.';
    }

    if (businessTypeId === 1 && !selectedDesignator?.label) {
      newErrors.designator = 'Please select a designator.';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const designatorId =
      businessTypeId === 1
        ? designatorList.find((item) => item.name === selectedDesignator?.label)?.id
        : '';
    dispatch(actions.update_business_details({ businessName, designatorId }));

    localStorage.setItem('selectedDesignator', JSON.stringify(selectedDesignator));

    const searchParams = new URLSearchParams(location.search);
    const editParam = searchParams.get("edit");
    if (editParam === "true") {
      navigate("/invoice");
    } else {
      navigate("/business_location");
    }
  };

  const handleKeyPress = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      await handleNextButtonClick(event);
    }
  };

  const backbtn = () => {
    if (businessTypeId === 2) {
      navigate("/services/corporation-formation");
    } else {
      navigate("/services/llc-formation");
    }
  };

  return (
    <div className="min-h-[75vh] bg-white py-12 lg:py-20 font-sans flex items-center">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          
          {/* Left Graphic */}
          <div className="lg:col-span-5 flex justify-center items-center">
            <img 
              src="/Assets/Form_img/business_name.png" 
              alt="Business Name" 
              className="w-full max-w-[340px] h-auto object-contain"
            />
          </div>

          {/* Right Form */}
          <div className="lg:col-span-7">
            <div className="mb-8">
              <span className="text-[#1a73e8] font-medium tracking-wider uppercase text-xs mb-2 block">
                Step 1 of Business Formation
              </span>
              <h1 className="text-3xl sm:text-4xl font-normal text-[#202124] tracking-tight mb-3">
                What's the name of your business?
              </h1>
              <p className="text-[15px] text-[#5f6368] leading-relaxed">
                Enter the official legal name you intend to register with the state.
              </p>
            </div>

            <form onKeyDown={handleKeyPress} className="space-y-6 max-w-xl">
              <div>
                <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                  Business Name <span className="text-[#d93025] font-semibold">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Acme Innovations"
                  onChange={handleBusinessNameChange}
                  value={businessName}
                  className={`w-full h-[48px] px-4 text-[15px] rounded-[6px] border ${
                    errors.businessName ? "border-[#d93025] focus:border-[#d93025] focus:ring-[#d93025]/20" : "border-[#dadce0] focus:border-[#1a73e8] focus:ring-[#1a73e8]/20"
                  } focus:ring-2 outline-none transition-all placeholder:text-[#9aa0a6] text-[#202124] bg-white`}
                />
                {errors.businessName && (
                  <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                    <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                    {errors.businessName}
                  </p>
                )}
              </div>

              {businessTypeId === 1 && (
                <div>
                  <label className="block text-[14px] font-medium text-[#3c4043] mb-2">
                    Select Legal Designator <span className="text-[#d93025] font-semibold">*</span>
                  </label>
                  <Select
                    showSearch
                    placeholder="Select Designator (e.g. LLC, L.L.C., Limited)"
                    size="large"
                    className="w-full h-[48px]"
                    options={designatorWithLabel.map((opt) => ({ label: opt.label, value: opt.label }))}
                    value={selectedDesignator?.label ?? null}
                    onChange={(val) => handleDesignatorChange(null, val ? { label: val } : null)}
                    status={errors.designator ? "error" : ""}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                  {errors.designator && (
                    <p className="text-[#d93025] text-[13px] mt-1.5 flex items-center gap-1 font-medium">
                      <svg className="w-3.5 h-3.5 inline flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                      {errors.designator}
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center gap-4 pt-6">
                <button
                  type="button"
                  onClick={backbtn}
                  className="px-6 py-2.5 rounded-[4px] border border-[#dadce0] text-[#1a73e8] font-medium hover:bg-[#f8f9fa] transition-colors flex items-center gap-2 cursor-pointer text-[14px]"
                >
                  <i className="fa-solid fa-arrow-left text-xs"></i> Back
                </button>
                <button
                  type="button"
                  onClick={handleNextButtonClick}
                  className="px-8 py-2.5 rounded-[4px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium shadow-sm transition-all flex items-center gap-2 cursor-pointer text-[14px]"
                >
                  Next <i className="fa-solid fa-arrow-right text-xs"></i>
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
}
